import { useState, type FormEvent, type ReactNode } from 'react';
import Sheet from './Sheet.js';
import { errorText } from './toast.js';

/**
 * A declarative add/edit form in a sheet, for the reference pages
 * (obligations, services, documents, belongings, trips, notes). Every value
 * is held as the string its input shows; the page converts on submit with the
 * helpers in `forms.ts`, so parsing (money as cents, blank as null) lives in
 * one tested place.
 */

export type FieldKind = 'text' | 'date' | 'datetime' | 'number' | 'money' | 'select' | 'textarea' | 'checkbox';

export interface Field {
  key: string;
  label: string;
  kind?: FieldKind;
  options?: readonly { value: string; label: string }[];
  wide?: boolean;
  required?: boolean;
  placeholder?: string;
  maxLength?: number;
  pattern?: string;
  hint?: string;
}

export type Values = Record<string, string>;

export default function FormSheet({
  title,
  fields,
  initial,
  submitLabel = 'Save',
  intro,
  danger,
  onSubmit,
  onClose,
}: {
  title: string;
  /** Shown above the fields (e.g. where a derived row's dates are changed). */
  intro?: ReactNode;
  /** A destructive action kept off the list rows (e.g. Delete): closes the sheet, then runs. */
  danger?: { label: string; onClick: () => void };
  fields: readonly Field[];
  initial: Values;
  submitLabel?: string;
  /** Throw to keep the sheet open with the error shown. */
  onSubmit: (values: Values) => Promise<void>;
  onClose: () => void;
}): JSX.Element {
  const [v, setV] = useState<Values>(initial);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const set = (k: string, value: string): void => setV((x) => ({ ...x, [k]: value }));

  async function submit(e: FormEvent): Promise<void> {
    e.preventDefault();
    const missing = fields.find((f) => f.required && !(v[f.key] ?? '').trim());
    if (missing) {
      setErr(`${missing.label} is required.`);
      return;
    }
    setBusy(true);
    setErr(null);
    try {
      await onSubmit(v);
      onClose();
    } catch (error) {
      setErr(errorText(error));
      setBusy(false);
    }
  }

  return (
    <Sheet title={title} onClose={onClose}>
      {intro && <div className="lo-sheet__hint">{intro}</div>}
      <form className="settings-form lo-form" onSubmit={submit} noValidate>
        {fields.map((f) => (
          <FieldInput key={f.key} field={f} value={v[f.key] ?? ''} onChange={(x) => set(f.key, x)} />
        ))}
        {err && (
          <p className="lo-form__error settings-field--wide" role="alert">
            {err}
          </p>
        )}
        <div className="lo-form__actions settings-field--wide">
          <button type="submit" className="btn" disabled={busy}>
            {busy ? 'Saving…' : submitLabel}
          </button>
          <button type="button" className="btn btn--ghost" onClick={onClose}>
            Cancel
          </button>
          {danger && (
            <button
              type="button"
              className="btn btn--ghost lo-btn--bad lo-form__danger"
              onClick={() => {
                onClose();
                danger.onClick();
              }}
            >
              {danger.label}
            </button>
          )}
        </div>
      </form>
    </Sheet>
  );
}

function FieldInput({ field: f, value, onChange }: { field: Field; value: string; onChange: (v: string) => void }): JSX.Element {
  const cls = `settings-field${f.wide || f.kind === 'textarea' ? ' settings-field--wide' : ''}`;
  if (f.kind === 'checkbox') {
    return (
      <label className={`lo-checkline ${cls}`}>
        <input type="checkbox" checked={value === 'true'} onChange={(e) => onChange(String(e.target.checked))} />
        <span>
          {f.label}
          {f.hint && <span className="lo-checkline__hint"> {f.hint}</span>}
        </span>
      </label>
    );
  }
  let input: JSX.Element;
  if (f.kind === 'select') {
    input = (
      <select className="settings-input" value={value} onChange={(e) => onChange(e.target.value)}>
        {(f.options ?? []).map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    );
  } else if (f.kind === 'textarea') {
    input = (
      <textarea
        className="settings-input lo-textarea"
        rows={3}
        value={value}
        placeholder={f.placeholder}
        onChange={(e) => onChange(e.target.value)}
      />
    );
  } else {
    const type =
      f.kind === 'date' ? 'date' : f.kind === 'datetime' ? 'datetime-local' : f.kind === 'number' ? 'number' : 'text';
    input = (
      <input
        className="settings-input"
        type={type}
        inputMode={f.kind === 'money' ? 'decimal' : f.kind === 'number' ? 'numeric' : undefined}
        value={value}
        placeholder={f.placeholder ?? (f.kind === 'money' ? '0.00' : undefined)}
        maxLength={f.maxLength}
        pattern={f.pattern}
        onChange={(e) => onChange(e.target.value)}
      />
    );
  }
  return (
    <label className={cls}>
      {f.label}
      {input}
      {f.hint && <span className="lo-field__hint">{f.hint}</span>}
    </label>
  );
}
